import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { take } from 'rxjs';
import { AlertService } from '../../../../atoms/alert/alert.service';
import { CreateBriefService } from '../../../briefs/service/create-brief/create-brief.service';
import { ComplianceDocsService } from '../../../../services/compliance-docs/compliance-docs.service';
import { CurrentUserService } from '../../../../common/current-user/current-user.service';
import { ScreeningExpectedAnswer, ScreeningQuestionTypeEnum } from '../../../../common/general-types/briefs.dto';
import { BbField } from '../../ui/bb-field/bb-field';
import { BbSegmented, BbSegmentedOption } from '../../ui/bb-segmented/bb-segmented';
import { detectScreeningPii, SCREENING_PII_ALERT } from './screening-pii.helpers';

interface ComplianceDoc {
  id: string;
  complianceName: string;
  subHeading?: string;
  description?: string;
  complianceContent?: string;
  complianceDocumentUrl?: string;
}

type NumericalOperator = 'eq' | 'gt' | 'lt';

/** Transient per-question editor state for the expected-answer controls. */
interface QuestionEdit {
  yesNo: 'Yes' | 'No' | null;
  operator: NumericalOperator;
  valueRaw: string;
}

@Component({
  selector: 'bb-screening-section',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BbField, BbSegmented],
  templateUrl: './screening-section.html',
  styleUrl: './screening-section.scss',
})
export class ScreeningSection implements OnInit {
  readonly service = inject(CreateBriefService);
  private readonly alertService = inject(AlertService);
  private readonly complianceService = inject(ComplianceDocsService);
  private readonly currentUserService = inject(CurrentUserService);

  readonly TypeEnum = ScreeningQuestionTypeEnum;
  readonly contentModeOptions: BbSegmentedOption[] = [
    { value: 'write', label: 'Write content' },
    { value: 'link', label: 'Link a document' },
  ];

  readonly answerTypeOptions: BbSegmentedOption[] = [
    { value: ScreeningQuestionTypeEnum.OneLine, label: 'Short text' },
    { value: ScreeningQuestionTypeEnum.YesNo, label: 'Yes / No' },
    { value: ScreeningQuestionTypeEnum.Numerical, label: 'Number' },
  ];
  readonly yesNoOptions: BbSegmentedOption[] = [
    { value: 'Yes', label: 'Yes' },
    { value: 'No', label: 'No' },
  ];
  readonly operatorOptions: BbSegmentedOption[] = [
    { value: 'gt', label: '>' },
    { value: 'lt', label: '<' },
    { value: 'eq', label: '=' },
  ];

  private readonly TYPE_LABEL: Record<ScreeningQuestionTypeEnum, string> = {
    [ScreeningQuestionTypeEnum.OneLine]: 'Short text',
    [ScreeningQuestionTypeEnum.YesNo]: 'Yes / No',
    [ScreeningQuestionTypeEnum.Numerical]: 'Number',
  };
  private readonly TYPE_HINT: Record<ScreeningQuestionTypeEnum, string> = {
    [ScreeningQuestionTypeEnum.OneLine]: 'Creators enter a single line of text.',
    [ScreeningQuestionTypeEnum.YesNo]: 'Creators answer Yes or No.',
    [ScreeningQuestionTypeEnum.Numerical]: 'Creators type a number when they apply.',
  };
  private readonly OP_WORD: Record<NumericalOperator, string> = {
    gt: 'greater than',
    lt: 'less than',
    eq: 'equal to',
  };

  /** Transient editor state per question id (operator/value survive a blank number, type switches). */
  readonly qEdit = signal<Record<string, QuestionEdit>>({});

  readonly library = signal<ComplianceDoc[]>([]);
  readonly adderMode = signal<'library' | 'new' | null>(null);
  readonly contentMode = signal<'write' | 'link'>('write');
  readonly saving = signal<boolean>(false);
  readonly draft = signal<ComplianceDoc>(this.emptyDraft());

  readonly attachedDocs = computed<ComplianceDoc[]>(() => {
    const ids = this.service.selectedComplianceIds();
    return this.library().filter((doc) => ids.includes(doc.id));
  });

  readonly availableDocs = computed<ComplianceDoc[]>(() => {
    const ids = this.service.selectedComplianceIds();
    return this.library().filter((doc) => !ids.includes(doc.id));
  });

  ngOnInit(): void {
    this.loadLibrary();
    this.seedEditState();
  }

  private seedEditState(): void {
    const seed: Record<string, QuestionEdit> = {};
    for (const q of this.service.screeningQuestions()) {
      seed[q.id] = this.deriveEdit(q.expected);
    }
    this.qEdit.set(seed);
  }

  private deriveEdit(expected: ScreeningExpectedAnswer | undefined): QuestionEdit {
    return {
      yesNo: expected?.kind === 'exact' ? expected.value : null,
      operator: expected?.kind === 'number' ? expected.operator : 'gt',
      valueRaw: expected?.kind === 'number' ? String(expected.value) : '',
    };
  }

  private emptyDraft(): ComplianceDoc {
    return { id: '', complianceName: '', subHeading: '', description: '', complianceContent: '', complianceDocumentUrl: '' };
  }

  private loadLibrary(refresh = false): void {
    const brandId = this.currentUserService.focussedBrand?.id;
    if (!brandId) {
      return;
    }
    this.complianceService
      .getBrandComplianceDocs(brandId, refresh)
      .pipe(take(1))
      .subscribe((docs) => this.library.set((docs as ComplianceDoc[]) ?? []));
  }

  isLinked(doc: ComplianceDoc): boolean {
    return !!doc.complianceDocumentUrl && !doc.complianceContent;
  }

  addQuestion(): void {
    const id = 's' + Date.now();
    this.qEdit.update((state) => ({ ...state, [id]: { yesNo: null, operator: 'gt', valueRaw: '' } }));
    this.service.screeningQuestions.update((list) => [
      ...list,
      { id, question: '', type: ScreeningQuestionTypeEnum.YesNo, expected: undefined },
    ]);
  }

  removeQuestion(index: number): void {
    const removed = this.service.screeningQuestions()[index];
    this.service.screeningQuestions.update((list) => list.filter((_, i) => i !== index));
    if (removed) {
      this.qEdit.update((state) => {
        const next = { ...state };
        delete next[removed.id];
        return next;
      });
    }
  }

  updateQuestion(index: number, value: string): void {
    this.service.screeningQuestions.update((list) => list.map((q, i) => (i === index ? { ...q, question: value } : q)));
  }

  questionInputValue(event: Event): string {
    return (event.target as HTMLInputElement).value;
  }

  onQuestionBlur(index: number): void {
    const question = this.service.screeningQuestions()[index];
    if (!question || !detectScreeningPii(question.question)) {
      return;
    }
    void this.alertService.alert(SCREENING_PII_ALERT);
    this.removeQuestion(index);
  }

  updateQuestionType(index: number, value: string): void {
    this.service.screeningQuestions.update((list) =>
      list.map((q, i) => (i === index ? { ...q, type: value as ScreeningQuestionTypeEnum } : q)),
    );
    this.commitExpected(index);
  }

  // ── Expected-answer editing ────────────────────────────────────────────────

  /** Yes/No toggle: clicking the active value clears it (returns to manual review). */
  setYesNo(index: number, id: string, value: string): void {
    this.qEdit.update((state) => {
      const current = state[id] ?? { yesNo: null, operator: 'gt' as NumericalOperator, valueRaw: '' };
      const next = current.yesNo === value ? null : (value as 'Yes' | 'No');
      return { ...state, [id]: { ...current, yesNo: next } };
    });
    this.commitExpected(index);
  }

  setOperator(index: number, id: string, operator: string): void {
    this.qEdit.update((state) => {
      const current = state[id] ?? { yesNo: null, operator: 'gt' as NumericalOperator, valueRaw: '' };
      return { ...state, [id]: { ...current, operator: operator as NumericalOperator } };
    });
    this.commitExpected(index);
  }

  setNumber(index: number, id: string, raw: string): void {
    this.qEdit.update((state) => {
      const current = state[id] ?? { yesNo: null, operator: 'gt' as NumericalOperator, valueRaw: '' };
      return { ...state, [id]: { ...current, valueRaw: raw } };
    });
    this.commitExpected(index);
  }

  /** Recompute the persisted `expected` for a question from its transient edit state. */
  private commitExpected(index: number): void {
    this.service.screeningQuestions.update((list) =>
      list.map((q, i) => (i === index ? { ...q, expected: this.computeExpected(q.type, this.qEdit()[q.id]) } : q)),
    );
  }

  private computeExpected(type: ScreeningQuestionTypeEnum, edit: QuestionEdit | undefined): ScreeningExpectedAnswer | undefined {
    if (!edit) {
      return undefined;
    }
    if (type === ScreeningQuestionTypeEnum.YesNo && edit.yesNo) {
      return { kind: 'exact', value: edit.yesNo };
    }
    if (type === ScreeningQuestionTypeEnum.Numerical && edit.valueRaw.trim() !== '') {
      const value = Number(edit.valueRaw);
      if (Number.isFinite(value)) {
        return { kind: 'number', operator: edit.operator, value };
      }
    }
    return undefined;
  }

  // ── Template helpers ────────────────────────────────────────────────────────

  typeLabel(type: ScreeningQuestionTypeEnum): string {
    return this.TYPE_LABEL[type] ?? '';
  }

  typeHint(type: ScreeningQuestionTypeEnum): string {
    return this.TYPE_HINT[type] ?? '';
  }

  editFor(id: string): QuestionEdit {
    return this.qEdit()[id] ?? { yesNo: null, operator: 'gt', valueRaw: '' };
  }

  /** Pass-pill sentence for the current selection, or '' when nothing auto-qualifies. */
  passText(type: ScreeningQuestionTypeEnum, id: string): string {
    const expected = this.computeExpected(type, this.qEdit()[id]);
    if (expected?.kind === 'exact') {
      return `Auto-qualifies creators who answer ${expected.value}.`;
    }
    if (expected?.kind === 'number') {
      return `Auto-qualifies when the answer is ${this.OP_WORD[expected.operator]} ${expected.value}.`;
    }
    return '';
  }

  openLibrary(): void {
    this.adderMode.set('library');
    this.loadLibrary();
  }

  attach(id: string): void {
    if (!this.service.selectedComplianceIds().includes(id)) {
      this.service.selectedComplianceIds.update((ids) => [...ids, id]);
    }
    this.adderMode.set(null);
  }

  detach(id: string): void {
    this.service.selectedComplianceIds.update((ids) => ids.filter((existing) => existing !== id));
  }

  cancelNew(): void {
    this.adderMode.set(null);
    this.draft.set(this.emptyDraft());
    this.contentMode.set('write');
  }

  setDraft(key: keyof ComplianceDoc, value: string): void {
    this.draft.update((current) => ({ ...current, [key]: value }));
  }

  saveNew(): void {
    const brandId = this.currentUserService.focussedBrand?.id;
    const draft = this.draft();
    if (!draft.complianceName.trim() || !brandId) {
      return;
    }
    this.saving.set(true);
    this.complianceService
      .createComplianceDoc({
        complianceName: draft.complianceName,
        subHeading: draft.subHeading || undefined,
        description: draft.description || undefined,
        complianceContent: this.contentMode() === 'write' ? draft.complianceContent || undefined : undefined,
        complianceDocumentUrl: this.contentMode() === 'link' ? draft.complianceDocumentUrl || undefined : undefined,
        hilOn: false,
        businessAccountId: brandId,
      })
      .pipe(take(1))
      .subscribe({
        next: (created) => {
          const id = (created as { id?: string })?.id;
          this.loadLibrary(true);
          if (id) {
            this.service.selectedComplianceIds.update((ids) => (ids.includes(id) ? ids : [...ids, id]));
          }
          this.saving.set(false);
          this.cancelNew();
        },
        error: () => this.saving.set(false),
      });
  }
}
